import {
  AmendmentWidgetField,
  buildAmendmentRowFieldKey,
  WidgetFieldExtractor,
} from './amendment-field.model';
import { asArray, asRecord, normalizeValue, readKey } from './amendment-value.util';

/**
 * Field extractors for the EMR widgets. Each one mirrors the field keys its widget template
 * passes to the amendment indicator, so a changed id always lands on a rendered slot.
 *
 * The widgets rebuild and normalize their stored rows when they initialize, so values are read
 * tolerantly: missing keys and empty strings are the same empty value, legacy key spellings are
 * accepted, and derived labels and ids are ignored.
 */

const VITALS_FIELD_KEYS: readonly string[] = [
  'height_ft',
  'height_in',
  'weight',
  'goal_weight',
  'body_fat_percentage',
  'blood_pressure',
  'pulse',
  'bmr',
  'respiration',
  'tdee',
  'fitzpatrick_scale',
  'vital_notes',
  'waist',
  'hip',
  'chest_bust',
  'left_arm',
  'right_arm',
  'left_thigh',
  'right_thigh',
  'neck',
  'vtc',
  'last_menstrual',
];

const EQUIPMENT_FIELD_KEYS: readonly string[] = [
  'equipmentType',
  'time',
  'quantity',
  'unit',
  'notes',
  ...Array.from({ length: 10 }, (_, index) => `attribute${index + 1}`),
];

const SHOT_FIELD_KEYS: readonly string[] = [
  'date',
  'compound',
  'amount',
  'uom',
  'site',
  'employee_id',
  'lot_number',
  'expire',
  'notes',
];

const SOAP_SECTIONS: readonly string[] = ['subjective', 'objective', 'assessment', 'plan'];
const SOAP_ROW_FIELD_KEYS: readonly string[] = ['date', 'employee', 'priority', 'note'];

/**
 * Laser template field keys mapped to the stored row keys.
 */
const LASER_FIELD_KEYS: Readonly<Record<string, string>> = {
  procedureType: 'procedure_type_id',
  equipment: 'equipment_id',
  serviceTime: 'service_time',
  smartCool: 'smart_cool',
  joules: 'joules',
  pulse: 'pulse',
  repetitionRate: 'rep_rate',
  firstLaserOption: 'laser_option_1_id',
  secondLaserOption: 'laser_option_2_id',
  note: 'notes',
};

const extractVitals: WidgetFieldExtractor = (properties) => {
  const values = asRecord(properties['value']);

  return VITALS_FIELD_KEYS.map((fieldKey) => ({
    fieldKey,
    value: normalizeValue(values?.[fieldKey]),
  }));
};

const extractEquipment: WidgetFieldExtractor = (properties) =>
  rows(properties).flatMap((row, rowIndex) =>
    EQUIPMENT_FIELD_KEYS.map((fieldKey) => ({
      fieldKey: buildAmendmentRowFieldKey(rowIndex, fieldKey),
      value: normalizeValue(readKey(row, fieldKey) ?? readKey(row, pascalCase(fieldKey))),
    })),
  );

const extractDemographics: WidgetFieldExtractor = (properties) =>
  rows(properties).flatMap((category, categoryIndex) => {
    const fields = readKey(category, 'items');

    if (!Array.isArray(fields)) {
      return [{ fieldKey: `c${categoryIndex}`, value: normalizeValue(readKey(category, 'value')) }];
    }

    return fields.map((field, fieldIndex) => ({
      fieldKey: `c${categoryIndex}:f${fieldIndex}`,
      value: normalizeValue(readKey(field, 'value')),
    }));
  });

const extractInsurance: WidgetFieldExtractor = (properties) =>
  rows(properties).flatMap((container, containerIndex) => {
    const containerKey = stringOr(readKey(container, 'name'), `c${containerIndex}`);

    return asArray(readKey(container, 'items')).flatMap((entry, entryIndex) =>
      asArray(readKey(entry, 'items')).flatMap((group, groupIndex) =>
        asArray(readKey(group, 'items')).map((field, fieldIndex) => ({
          fieldKey: buildInsuranceFieldKey(
            containerKey,
            entryIndex,
            groupIndex,
            stringOr(readKey(field, 'name'), `f${fieldIndex}`),
          ),
          value: normalizeValue(readKey(field, 'value')),
        })),
      ),
    );
  });

const extractShots: WidgetFieldExtractor = (properties) => {
  const configuredKeys = asArray(readKey(properties['items'], 'fields'))
    .map((field) => readKey(field, 'name'))
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
  const fieldKeys = configuredKeys.length ? configuredKeys : SHOT_FIELD_KEYS;

  return rows(properties).flatMap((row, rowIndex) =>
    fieldKeys.map((fieldKey) => ({
      fieldKey: buildAmendmentRowFieldKey(rowIndex, fieldKey),
      value: normalizeValue(readKey(row, fieldKey)),
    })),
  );
};

const extractSoapNotes: WidgetFieldExtractor = (properties) => {
  const items = asRecord(readKey(properties['items'], 'value'));
  const fields: AmendmentWidgetField[] = [];

  for (const section of SOAP_SECTIONS) {
    asArray(items?.[section]).forEach((row, rowIndex) => {
      for (const fieldKey of SOAP_ROW_FIELD_KEYS) {
        fields.push({
          fieldKey: `${section}:${buildAmendmentRowFieldKey(rowIndex, fieldKey)}`,
          value: normalizeValue(readKey(row, fieldKey)),
        });
      }
    });

    fields.push({
      fieldKey: `${section}_attributes`,
      value: normalizeValue(items?.[`${section}_attributes`]),
    });
  }

  fields.push({
    fieldKey: 'plan_items',
    value: normalizeValue(
      asArray(items?.['plan_items']).map((item) => readKey(item, 'id') ?? item),
    ),
  });
  fields.push({
    fieldKey: 'plan_items_quantity',
    value: normalizeValue(items?.['plan_items_quantity']),
  });

  return fields;
};

const extractLaser: WidgetFieldExtractor = (properties) =>
  rows(properties).flatMap((row, rowIndex) =>
    Object.entries(LASER_FIELD_KEYS).map(([fieldKey, storedKey]) => ({
      fieldKey: buildAmendmentRowFieldKey(rowIndex, fieldKey),
      value: normalizeValue(readKey(row, storedKey)),
    })),
  );

const extractMedicalNotes: WidgetFieldExtractor = (properties) =>
  rows(properties).map((note, rowIndex) => ({
    fieldKey: buildAmendmentRowFieldKey(rowIndex),
    value: normalizeValue({
      date: readKey(note, 'date'),
      employee_id: readKey(note, 'employee_id') ?? readKey(note, 'employee'),
      text: readKey(note, 'text'),
    }),
  }));

const extractMedicalHistory: WidgetFieldExtractor = (properties) =>
  rows(properties).flatMap((category, categoryIndex) => {
    const categoryKey = stringOr(readKey(category, 'name'), `c${categoryIndex}`);
    const type = readKey(category, 'type');

    if (type === 'checkbox-list') {
      return [
        {
          fieldKey: categoryKey,
          value: asArray(readKey(category, 'items')).map((item) => Boolean(readKey(item, 'value'))),
        },
      ];
    }

    if (type === 'input-list') {
      return asArray(readKey(category, 'value')).flatMap((row, rowIndex) =>
        asArray(row).map((field, fieldIndex) => ({
          fieldKey: `${categoryKey}:${buildAmendmentRowFieldKey(rowIndex, `f${fieldIndex}`)}`,
          value: normalizeValue(readKey(field, 'value')),
        })),
      );
    }

    if (type === 'table') {
      const columns = asArray(readKey(category, 'items'))
        .map((column) => readKey(column, 'title'))
        .filter((title): title is string => typeof title === 'string' && title.length > 0);

      return asArray(readKey(category, 'value')).flatMap((row, rowIndex) =>
        columns.map((column) => ({
          fieldKey: `${categoryKey}:${buildAmendmentRowFieldKey(rowIndex, column)}`,
          value: normalizeValue(readKey(row, column)),
        })),
      );
    }

    return [];
  });

export const EMR_WIDGET_FIELD_EXTRACTORS: Readonly<Record<string, WidgetFieldExtractor>> = {
  'fb-emr-vitals': extractVitals,
  'fb-emr-equipment': extractEquipment,
  'fb-emr-demographics': extractDemographics,
  'fb-emr-insurance-info': extractInsurance,
  'fb-emr-shots': extractShots,
  'fb-emr-soap-notes': extractSoapNotes,
  'fb-emr-laser': extractLaser,
  'fb-emr-medical-notes': extractMedicalNotes,
  'fb-emr-medical-history': extractMedicalHistory,
};

/**
 * Builds the field key of one insurance field; the same shape the insurance template uses.
 */
export function buildInsuranceFieldKey(
  containerName: string,
  entryIndex: number,
  groupIndex: number,
  fieldName: string,
): string {
  return `${containerName}:e${entryIndex}:g${groupIndex}:${fieldName}`;
}

/**
 * The repeatable rows stored under `properties.items.value`. A hidden widget stores null there.
 */
function rows(properties: Record<string, unknown>): unknown[] {
  return asArray(readKey(properties['items'], 'value'));
}

function pascalCase(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}
